import classNames from 'classnames';
import { useEffect, useId, useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';

import { useUiMessages } from '../../messages';
import { CloseButton } from '../CloseButton';
import { Typography } from '../Typography';

import { FeaturedIcon } from './FeaturedIcon';
import type { FeaturedIconVariant } from './FeaturedIcon';
import { focusInitial, wrapTabFocus } from './focus';
import styles from './Modal.module.css';
import { ModalFooter } from './ModalFooter';
import type { ModalAction, ModalFooterLayout, ModalSettings } from './ModalFooter';

export type { ModalAction, ModalFooterLayout, ModalSettings } from './ModalFooter';
export type ModalVariant = FeaturedIconVariant;
export type ModalHeaderLayout = 'top' | 'top-center' | 'left';

export interface ModalLabels {
  close: string;
  dontShowAgain: string;
  settings: string;
}

const DEFAULT_LABELS: ModalLabels = {
  close: 'Close',
  dontShowAgain: "Don't show again",
  settings: 'Settings',
};

export interface ModalProps {
  /** Render the modal. Defaults to `true` (the provider mounts/unmounts entries itself). */
  open?: boolean;
  /** Requested dismissal — the close button, Escape, or an action with `closeOnClick`. */
  onClose?: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Featured icon tint + default glyph. */
  variant?: ModalVariant;
  /** Custom header glyph (any node), or `false` to drop the featured icon entirely. */
  icon?: ReactNode | false;
  headerLayout?: ModalHeaderLayout;
  /** Optional scrollable content between header and footer. */
  body?: ReactNode;
  actions?: ModalAction[];
  footerLayout?: ModalFooterLayout;
  showClose?: boolean;
  /** Show the `Don't show again` checkbox in the footer cluster. */
  dontShowAgain?: boolean;
  dontShowAgainChecked?: boolean;
  onDontShowAgainChange?: (checked: boolean) => void;
  settings?: ModalSettings;
  /** Escape requests a close (default true). Overlay dismissal is the provider's concern. */
  dismissible?: boolean;
  /**
   * Set by `ModalProvider`: it owns focus, Escape and scroll-lock for the whole stack, so the
   * surface only keeps Tab trapped inside itself. Leave false for standalone/declarative use.
   */
  managed?: boolean;
  labels?: Partial<ModalLabels>;
  className?: string;
  id?: string;
}

/**
 * Presentational modal surface: featured icon + title + supporting text + close, an optional
 * scrollable body, and a customizable action footer. `role="dialog"` with `aria-modal`, wired
 * `aria-labelledby`/`aria-describedby`, and Tab kept inside the card. Standalone it also grabs
 * initial focus, restores it on unmount, locks body scroll and closes on Escape; under
 * `ModalProvider` (`managed`) those belong to the provider so a stack behaves as one dialog.
 *
 * @interim Seeded from the Figma "Universal Modal"; replaceable by the UI Kit without API breakage.
 */
export function Modal({
  open = true,
  onClose,
  title,
  description,
  variant = 'success',
  icon,
  headerLayout = 'top',
  body,
  actions = [],
  footerLayout = 'horizontal',
  showClose = true,
  dontShowAgain = false,
  dontShowAgainChecked,
  onDontShowAgainChange,
  settings,
  dismissible = true,
  managed = false,
  labels,
  className,
  id,
}: ModalProps) {
  const messages = useUiMessages();
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const titleId = `${baseId}-title`;
  const descId = `${baseId}-desc`;
  const cardRef = useRef<HTMLDivElement>(null);
  const t = { ...DEFAULT_LABELS, ...messages.modal, ...labels };

  // Standalone lifecycle: initial focus + restore, body scroll-lock. Managed → provider owns these.
  useEffect(() => {
    if (!open || managed) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    focusInitial(cardRef.current);
    const { body: pageBody } = document;
    const priorOverflow = pageBody.style.overflow;
    pageBody.style.overflow = 'hidden';
    return () => {
      pageBody.style.overflow = priorOverflow;
      previous?.focus();
    };
  }, [open, managed]);

  if (!open) return null;

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && !managed && dismissible) {
      event.stopPropagation();
      onClose?.();
      return;
    }
    if (event.key === 'Tab') wrapTabFocus(cardRef.current, event.nativeEvent);
  };

  const handleAction = (action: ModalAction) => {
    action.onClick?.();
    if (!managed && action.closeOnClick !== false) onClose?.();
  };

  const hasFooter = actions.length > 0 || dontShowAgain || settings != null;

  return (
    // The dialog owns Escape + Tab focus-trapping; it isn't a natively-interactive element.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description != null ? descId : undefined}
      className={classNames(styles.card, className)}
      onKeyDown={onKeyDown}
    >
      <div className={styles.header} data-layout={headerLayout}>
        {icon !== false ? <FeaturedIcon variant={variant} icon={icon} /> : null}
        <div className={styles.heading}>
          <Typography
            as="h2"
            id={titleId}
            variant="text-md"
            weight="semibold"
            className={classNames(styles.title)}
          >
            {title}
          </Typography>
          {description != null ? (
            <Typography
              as="p"
              id={descId}
              variant="text-sm"
              className={classNames(styles.description)}
            >
              {description}
            </Typography>
          ) : null}
        </div>
        {showClose ? (
          <CloseButton size="md" label={t.close} className={styles.close} onClick={onClose} />
        ) : null}
      </div>

      {body != null ? <div className={styles.body}>{body}</div> : null}

      {hasFooter ? (
        <ModalFooter
          actions={actions}
          layout={footerLayout}
          onAction={handleAction}
          dontShowAgain={dontShowAgain}
          labels={{ dontShowAgain: t.dontShowAgain, settings: t.settings }}
          {...(dontShowAgainChecked !== undefined ? { dontShowAgainChecked } : {})}
          {...(onDontShowAgainChange != null ? { onDontShowAgainChange } : {})}
          {...(settings != null ? { settings } : {})}
        />
      ) : null}
    </div>
  );
}
